/* eslint-disable no-useless-escape */
/* eslint-disable array-callback-return */
import axios from 'axios';
import moment from 'moment';
import { anyPass, isEmpty, isNil } from 'ramda';
import { string } from 'yup';
import { BASE_URL } from './constants/api-urls';
import { getAuthAccountUserId } from './utils/authAccount';
import {
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN,
  SPOTIFY_TOKEN_EXPIRES_AT,
} from './constants/storage';
import { rearrangeArrayWithLock } from './utils/utils';
import { toast } from 'react-toastify';

export const isEmptyOrNil = anyPass([isEmpty, isNil]);

export function calculateStreamsFromBudget(budget: string | number): number {
  const price = Number(budget);
  if (price >= 100 && price < 500) {
    return Math.round(price / 0.03);
  } else if (price >= 500 && price < 1000) {
    return Math.round(price / 0.025);
  } else if (price >= 1000 && price < 2500) {
    return Math.round(price / 0.0225);
  } else if (price >= 2500 && price <= 5000) {
    return Math.round(price / 0.02);
  }
  return Math.round(price / 0.03);
}

export const deleteAllRefresh = async (playlistId: string) => {
  const response = await axios.delete(`${BASE_URL}/deleterefresh/${playlistId}`);
  return response;
};

export const addSongToPlaylist = async (
  playlistId: string[],
  songUri: string,
  token: string,
  refreshToken: string,
  userId = '',
  songPosition = 0,
  startTime = null,
  endTime = null,
  timing: string,
  notes: string,
  campaignId?: string,
  artist_name?: string,
  song_name?: string,
  profileObjectId?: string
) => {
  try {
    const hasScheduledStart = Boolean(startTime && startTime !== 'null');
    const renderedStartTime = hasScheduledStart
      ? new Date(startTime as any)?.toISOString()
      : new Date().toISOString();
    const renderedEndTime = endTime && endTime !== 'null' ? new Date(endTime as any)?.toISOString() : null;

    // Get Auth0 user ID and profile info from localStorage
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
    const auth0UserId = userInfo?.userId || getAuthAccountUserId() || '';

    // If on shared profile, use owner's profile ObjectId, otherwise use accessor's Auth0 ID
    const profileIdentifier = profileObjectId
      || (spotifyUser.isShared === true && spotifyUser.profileId
        ? spotifyUser.profileId
        : auth0UserId);

    const refreshTokenForRequest = profileObjectId ? '_' : refreshToken;

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
      data: { notes, artist_name, song_name, campaignId },
      _id: auth0UserId,
      profileId: profileIdentifier,
    };

    const createRequestCall = (singlePlaylist: string) =>
      axios.post(
        `${BASE_URL}/createRequest/${
          hasScheduledStart ? 'wait' : 'started'
        }/${userId}/${refreshTokenForRequest}/${singlePlaylist}/${songUri}/${
          songPosition === 0 ? songPosition : songPosition - 1
        }/${renderedStartTime}/${renderedEndTime}/${timing}/${moment()}`,
        config
      );

    // Future/scheduled placement: store request only.
    if (hasScheduledStart) {
      const scheduledRequests = playlistId.map(singlePlaylist => createRequestCall(singlePlaylist));
      return await Promise.all(scheduledRequests);
    }

    // Immediate placement: add to Spotify first, then persist tracking row (including optional end date).
    const immediateRequests = playlistId.map(async singlePlaylist => {
      await axios(
        `https://api.spotify.com/v1/playlists/${singlePlaylist}/items?position=${
          songPosition === 0 ? songPosition : songPosition - 1
        }&uris=${songUri}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return createRequestCall(singlePlaylist);
    });

    return await Promise.all(immediateRequests);
  } catch (err) {
    console.error('addSongToPlaylist failed:', err);
    throw err;
  }
};

export const addRestoredSongToPlaylist = async (
  playlistId: string[],
  songUri: string,
  token: string,
  refreshToken: string,
  userId = '',
  songPosition = 0,
  startTime = null,
  endTime = null,
  timing: string,
  notes: string,
  type?: string
) => {
  const addSongs = playlistId.map(singlePlaylist => {
    axios(
      `https://api.spotify.com/v1/playlists/${singlePlaylist}/items?position=${songPosition}&uris=${songUri}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    ).then(async repsosive => {
      const renderedStartTime = new Date();
      const renderedEndTime = endTime ? new Date(endTime)?.toISOString() : null;
      
      // Get Auth0 user ID and profile info from localStorage
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
      const auth0UserId = userInfo?.userId || getAuthAccountUserId() || '';
      
      // If on shared profile, use owner's profile ObjectId, otherwise use accessor's Auth0 ID
      const profileIdentifier = spotifyUser.isShared === true && spotifyUser.profileId 
        ? spotifyUser.profileId  // Owner's profile ObjectId for shared profiles
        : auth0UserId;            // Accessor's Auth0 ID for own profiles
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
        data: { notes },
        _id: auth0UserId,           // Always send accessor's Auth0 ID for access validation
        profileId: profileIdentifier, // Send profile identifier (ObjectId for shared, Auth0 ID for own)
      };
      if (!endTime) {
        const responsees = axios.post(
          `${BASE_URL}/createRequest/started/${userId}/${refreshToken}/${singlePlaylist}/${songUri}/${songPosition}/${renderedStartTime}/${renderedEndTime}/${timing}/${moment()}`,
          config
        );
        return responsees;
      }
    });
  });
  const responseData = await Promise.all(addSongs);
  return responseData;
};

export const getPlaylistItemsSpotify = async (playlistId, token) => {
  const response = await axios(`https://api.spotify.com/v1/playlists/${playlistId}/items`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then(async response => {
    return response.data;
  });
  return response;
};

export const removeSongToPlaylist = async (
  playlistId: string[],
  songUri: string,
  token: string,
  refreshToken: string,
  userId = '',
  songPosition = 0,
  startTime = null,
  endTime = null,
  timing = string
) => {
  const addSongs = playlistId.map(singlePlaylist => {
    axios(
      `https://api.spotify.com/v1/playlists/${singlePlaylist}/items?position=${
        songPosition === 0 ? songPosition : songPosition - 1
      }&uris=${songUri}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    ).then(async repsosive => {});
  });
  const responseData = await Promise.all(addSongs);
  return responseData;
};
export const editSongToPlaylist = async (
  oldPlaylistId: string,
  playlistId: string,
  songUri: string,
  token: string,
  refreshToken: string,
  userId = '',
  songPosition = 0,
  startTime = null,
  endTime = null,
  timing: string,
  playId = null,
  indexes = null,
  notes = null,
  currentposition = 0
) => {
  const renderedStartTime = startTime && startTime !== 'null' ? moment(startTime) : null;
  const renderedEndTime = endTime && endTime !== 'null' ? new Date(endTime)?.toISOString() : null;

  // Get Auth0 user ID and profile info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
  const auth0UserId = userInfo?.userId || '';
  
  // If on shared profile, use owner's profile ObjectId, otherwise use accessor's Auth0 ID
  const profileIdentifier = spotifyUser.isShared === true && spotifyUser.profileId 
    ? spotifyUser.profileId  // Owner's profile ObjectId for shared profiles
    : auth0UserId;            // Accessor's Auth0 ID for own profiles

  const config = {
    indexes: indexes === 0 ? indexes : indexes - 1,
    notes: notes,
    headers: {
      'Content-Type': 'application/json',
    },
    _id: auth0UserId,           // Always send accessor's Auth0 ID for access validation
    profileId: profileIdentifier, // Send profile identifier (ObjectId for shared, Auth0 ID for own)
  };

  try {
    const response = await axios.post(
      `${BASE_URL}/editRequest/${
        startTime ? 'wait' : 'started'
      }/${userId}/${refreshToken}/${playlistId}/${songUri}/${
        !songPosition ? 0 : songPosition === 0 ? songPosition : songPosition - 1
      }/${renderedStartTime}/${renderedEndTime}/${timing}/${playId}/${oldPlaylistId}/${currentposition}`,
      config
    );

    console.log('editSongToPlaylist response:', response.data);
    return response;
  } catch (err) {
    console.error('editSongToPlaylist ERROR:', err);
    console.error('editSongToPlaylist ERROR response:', err.response?.data);
    throw err;
  }
};

export const editSongEndDateAndNotes = async (
  oldPlaylistId: string,
  playlistId: string,
  songUri: string,
  token: string,
  refreshToken: string,
  userId = '',
  songPosition = 0,
  startTime = null,
  endTime = null,
  timing: string,
  playId = null,
  indexes = null,
  notes = null,
  currentposition = 0
) => {
  const renderedStartTime = startTime && startTime !== 'null' ? moment(startTime) : null;

  const renderedEndTime = endTime && endTime !== 'null' ? new Date(endTime)?.toISOString() : null;

  const config = {
    indexes: indexes === 0 ? indexes : indexes - 1,
    notes: notes,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    console.log('editSongEndDateAndNotes request:', {
      playId,
      notes,
      indexes,
      endTime: renderedEndTime,
      url: `${BASE_URL}/editSongEndDateAndNotes/${
        startTime ? 'wait' : 'started'
      }/${userId}/${refreshToken}/${playlistId}/${songUri}/${
        !songPosition ? 0 : songPosition === 0 ? songPosition : songPosition - 1
      }/${renderedStartTime}/${renderedEndTime}/${timing}/${playId}/${oldPlaylistId}/${currentposition}`
    });

    const response = await axios.post(
      `${BASE_URL}/editSongEndDateAndNotes/${
        startTime ? 'wait' : 'started'
      }/${userId}/${refreshToken}/${playlistId}/${songUri}/${
        !songPosition ? 0 : songPosition === 0 ? songPosition : songPosition - 1
      }/${renderedStartTime}/${renderedEndTime}/${timing}/${playId}/${oldPlaylistId}/${currentposition}`,
      config
    );

    console.log('editSongEndDateAndNotes response:', response.data);
    return response;
  } catch (err) {
    console.error('editSongEndDateAndNotes ERROR:', err);
    console.error('editSongEndDateAndNotes ERROR response:', err.response?.data);
    throw err;
  }
};

export const getstripePrices = async () => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios.get(`${BASE_URL}/getPrices`, config);
  return response;
};

// Move track helper function for drag-and-drop reordering
export interface MoveTrackArgs {
  token: string;
  playlistId: string;
  rangeStart: number;
  insertBefore: number;
  rangeLength?: number; // Number of tracks to move (default: 1)
  snapshotId?: string;
}

export const moveTrack = async ({
  token,
  playlistId,
  rangeStart,
  insertBefore,
  rangeLength = 1, // Default to 1 for backward compatibility
  snapshotId,
}: MoveTrackArgs) => {
  const data: any = {
    range_start: rangeStart,
    insert_before: insertBefore,
    range_length: rangeLength,
  };

  // Include snapshot_id if provided for consistency checks
  if (snapshotId) {
    data.snapshot_id = snapshotId;
  }

  return axios.put(`https://api.spotify.com/v1/playlists/${playlistId}/items`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const LockSongPosition = async ({
  lock,
  position,
  playlist_id,
  user_id,
  song_uri,
  access_token,
  play_id,
}) => {
  const config = {
    lock,
    position,
    playlist_id,
    user_id,
    song_uri,
    access_token,
    play_id,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios
    .post(`${BASE_URL}/LockSongPosition`, config)
    .then()
    .catch(err => {});

  return response;
};

export const AddOrEditNotes = async (notes: string, id: string) => {
  const config = {
    _id: id,
    notes: notes,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios.post(`${BASE_URL}/add-notes-pre-release/`, config);

  return response;
};

export const AddOrEditEndDate = async (endTime: string, id: string) => {
  const renderedEndTime = endTime && endTime !== 'null' ? new Date(endTime)?.toISOString() : null;
  const config = {
    _id: id,
    end_time: renderedEndTime,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios.post(`${BASE_URL}/edit-enddate-pre-release/`, config);

  return response;
};

//Add User Profile
export const addUserProfileAction = async (
  name: string,
  spotifyID: string,
  image: string,
  code: string,
  token: string,
  refresh: string,
  userId: string
) => {
  try {
    // Best-effort Auth0 email for admin contact; never required for Spotify connect.
    let accountEmail: string | undefined;
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const normalized = userInfo?.email?.toString?.().trim?.().toLowerCase?.();
      if (normalized) {
        accountEmail = normalized;
      }
    } catch {
      // ignore
    }

    const config = {
      username: name,
      image: image,
      code: code,
      token: token,
      userId: userId,
      refresh: refresh,
      spotifyID,
      ...(accountEmail ? { email: accountEmail } : {}),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const response = await axios.post(`${BASE_URL}/user/add`, config);
    return response;
  } catch (error) {
    console.error('Unable to save Spotify profile:', error);
    throw error;
  }
};

export async function getRefreshedToken(refresh_token, profileIdentity?: any) {
  try {
    const refrechedToken = await axios.post(
      `${process.env.REACT_APP_BACKEND_URI}/api/spotify/refresh`,
      {
        refresh_token: `${refresh_token}`,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Persist rotated refresh token when Spotify returns one
    const newRefresh = refrechedToken?.data?.refresh_token;
    if (newRefresh) {
      addItemToStorage(SPOTIFY_REFRESH_TOKEN, newRefresh);
    }
    if (refrechedToken?.data?.access_token) {
      addItemToStorage(SPOTIFY_TOKEN, refrechedToken.data.access_token);
    }
    if (refrechedToken?.data?.expires_in) {
      const expiresAt = Date.now() + refrechedToken.data.expires_in * 1000;
      addItemToStorage(SPOTIFY_TOKEN_EXPIRES_AT, expiresAt.toString());
    }

    return refrechedToken;
  } catch (error) {
    // Lazy import avoids circular dependency with spotifyAuth → utils
    const { isSpotifyReauthError, clearSpotifySession, SpotifyReauthRequiredError } = await import(
      './utils/spotifyAuth'
    );
    if (isSpotifyReauthError(error)) {
      clearSpotifySession(profileIdentity || null, 'invalid_grant');
      throw new SpotifyReauthRequiredError(
        error?.response?.data?.message || 'Spotify reauthorization required'
      );
    }
    throw error;
  }
}

export async function Auth0Logout() {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const client_id = process.env.REACT_APP_AUTH0;
  const returnTo = window.location.origin;
  const { clearAuth0Session } = await import('./utils/auth0Session');
  clearAuth0Session();
  localStorage.removeItem('userInfo');
  window.location.replace(
    `https://${domain}/v2/logout?returnTo=${returnTo}&client_id=${client_id}`
  );
}

export const sendPasswordResetEmail = async (email: string) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const client_id = process.env.REACT_APP_AUTH0;

  try {
    const response = await axios.post(
      `https://${domain}/dbconnections/change_password`,
      {
        client_id,
        email,
        connection: 'Username-Password-Authentication',
      },
      {
        headers: {
          'content-type': 'application/json',
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error_description || 'Failed to send reset email',
    };
  }
};

export const addItemToStorage = (key: string, item: string) => {
  localStorage.setItem(key, item);
};

export const addItemToProfileStorage = (
  key: string,
  itemName: string,
  profile: string,
  code: string
) => {};

export const getItemFromStorage = (key: string) => {
  return localStorage.getItem(key);
};

export const deleteItemFromStorage = (key: string) => {
  return localStorage.removeItem(key);
};

export async function syncDataFromSpotify({
  token,
  setPlaylist: playlistId,
  scheduledata,
  alldata,
  syncModal,
  setSyncModal,
  currentRetry = 0,
}) {
  const allSongs = alldata.length ? alldata : await getAllTracks(token, playlistId);
  let latestdata = allSongs.map((song, index) => {
    let schedule = scheduledata.filter(dataSong => song?.item?.uri === dataSong?.song_uri);
    let scheduleObj = schedule.length ? schedule.filter(val => val.lockPosition)[0] : false;
    return scheduleObj && scheduleObj?.lockPosition
      ? {
          ...song,
          lockPositionNumber: scheduleObj?.position,
          lockPosition: index === Number(scheduleObj?.position) ? false : true,
          position: index,
          name: song?.item?.name,
        }
      : { ...song, position: index, lockPosition: false };
  });
  await rearrange(token, playlistId, latestdata, scheduledata, setSyncModal, 6, currentRetry);
}

// Helper function to check for duplicate songs in a playlist
export const hasDuplicateSongs = allSongs => {
  if (!allSongs || !Array.isArray(allSongs) || allSongs.length === 0) {
    return false;
  }

  const uriMap = new Map();

  for (let song of allSongs) {
    let uri = null;

    // Handle different data structures:
    // 1. Raw Spotify API data: song.item.uri
    // 2. Processed playlist data: check if there's a songUri or other URI field
    if (song?.item?.uri) {
      uri = song.item.uri;
    } else if (song?.songUri) {
      uri = song.songUri;
    } else if (song?.uri) {
      uri = song.uri;
    } else if (song?.rowNumber?.songUri) {
      uri = song.rowNumber.songUri;
    }

    if (uri) {
      if (uriMap.has(uri)) {
        return true; // Found duplicate
      }
      uriMap.set(uri, true);
    }
  }

  return false; // No duplicates found
};

export async function rearrange(
  token,
  playlistId,
  allSongs,
  scheduledata,
  setSyncModal,
  maxRetries = 6,
  currentRetry = 0
) {
  let isDuplicateMsgShown = false;
  let arrangesongs = await rearrangeArrayWithLock(allSongs).filter(
    obj => obj?.lockPosition && Number(obj?.lockPositionNumber) < allSongs?.length
  );

  arrangesongs?.map((a, i) => {
    let match = allSongs?.filter(obj => obj?.item?.uri === a?.item?.uri);
    if (match.length > 1) {
      if (!isDuplicateMsgShown) {
        toast.error('Unable to reset the playlist because it has duplicate songs');
        isDuplicateMsgShown = true;
      }
      setTimeout(() => {
        setSyncModal(false);
        window.location.reload();
      }, 2000);
      return;
    }
  });

  if (arrangesongs.length) {
    // Prevent infinite recursion - if we've tried too many times, just finish
    if (currentRetry >= maxRetries) {
      console.error('Max retries reached, finishing reset operation');
      setSyncModal(false);
      window.location.reload();
      return;
    }

    for (let i = 0; i < arrangesongs?.length; i++) {
      if (arrangesongs[i].lockPosition) {
        // Simplified Spotify API call - just move the song from current position to target position
        axios(`https://api.spotify.com/v1/playlists/${playlistId}/items`, {
          method: 'put',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            range_start: arrangesongs[i].position,
            insert_before: Number(arrangesongs[i].lockPositionNumber) + 1,
            range_length: 1,
          },
        })
          .then(() => {})
          .catch(err => {
            console.error('Error moving track:', err);
          });
      }
      if (arrangesongs.length - 1 === i) {
        await timeout(2000); // Increased timeout to give Spotify API more time
        // Only retry if we haven't reached max retries
        if (currentRetry < maxRetries) {
          syncDataFromSpotify({
            token,
            setPlaylist: playlistId,
            scheduledata,
            alldata: [], // This will fetch fresh data
            syncModal: false,
            setSyncModal,
            currentRetry: currentRetry + 1, // Pass retry count
          });
        } else {
          // Max retries reached, finish the operation
          setSyncModal(false);
          window.location.reload();
        }
      }
    }
  } else {
    // No songs to rearrange, operation complete
    setSyncModal(false);
    window.location.reload();
  }
}
export const dateFormationDDMMYY = date => {
  const pad = int => {
    return String(int).length > 1 ? int : `0${int}`;
  };
  let dd = new Date(date).getDate();
  let MM = new Date(date).getMonth() + 1;
  let yy = new Date(date).getFullYear();
  return `${pad(dd)}/${pad(MM)}/${pad(yy)}`;
};
export const getOriginalDate = (verisonsRecord, viewDetailData) => {
  if (verisonsRecord?.length) {
    let data = verisonsRecord[0];
    let uris = JSON.parse(data.uris);

    for (let i = 0; i < uris.length; i++) {
      for (let j = 0; j < uris[i]?.uris?.length; j++) {
        if (uris[i]?.uris[j]?.uri === viewDetailData?.item?.uri) {
          let orgDate = uris[i]?.uris[j].date;
          return dateFormationDDMMYY(orgDate);
        }
      }
    }
  }
};
export async function refreshPlaylist({
  token,
  setPlaylist: playlistId,
  playlist,
  currentPage,
  refreshSongs,
  recall,
  PlaylistRefreshRecord,
  saveRestore,
}) {
  try {
    let refreshRecordedUris = [];
    if (PlaylistRefreshRecord && PlaylistRefreshRecord.length && PlaylistRefreshRecord[0]?.uris) {
      let versions = [];
      JSON.parse(PlaylistRefreshRecord[0].uris).map((a, i) => {
        if (a.version) {
          versions.push(a);
          if (i === JSON.parse(PlaylistRefreshRecord[0].uris).length - 1) {
            refreshRecordedUris.push({
              playlist_id: playlistId,
              date: moment(),
              uris: versions,
            });
          }
        } else {
          if (i === JSON.parse(PlaylistRefreshRecord[0].uris).length - 1) {
            refreshRecordedUris.push({
              playlist_id: playlistId,
              date: moment(),
              uris: [{ version: 1, uris: JSON.parse(PlaylistRefreshRecord[0].uris) }],
            });
          }
        }
      });
    }

    const allSongs = await getAllTracks(token, playlistId);
    let uriWithoutlocal = allSongs.filter(song => song?.item?.uri.includes('spotify:track:'));
    const songUris = uriWithoutlocal.map(song => {
      return `${song?.item?.uri}`;
    });
    const songUri = allSongs.map((obj, i) => {
      return {
        ...obj,
        position: i,
      };
    });
    let refreshRecordedaddeddUri = PlaylistRefreshRecord[0]?.addeddUri
      ? JSON.parse(PlaylistRefreshRecord[0].addeddUri)
      : [];
    let recordedaddeddUri = refreshRecordedaddeddUri?.map(obj => obj?.uri) || [];

    let addeddsongUri = songUri
      .filter(obj => !recordedaddeddUri.includes(obj?.item?.uri))
      .map(song => {
        return { uri: `${song?.item?.uri}`, date: song?.added_at, position: song?.position };
      });
    let updatedVersionArr = [];
    if (refreshRecordedUris.length && refreshRecordedUris[0].uris) {
      updatedVersionArr = [...refreshRecordedUris[0].uris];
      let versionURIS = [];
      songUri.map((song, i) => {
        for (
          let i = 0;
          i < refreshRecordedUris[0]?.uris[refreshRecordedUris[0]?.uris?.length - 1]?.uris?.length;
          i++
        ) {
          let lastVersionSong =
            refreshRecordedUris[0]?.uris[refreshRecordedUris[0]?.uris?.length - 1]?.uris[i];
          if (lastVersionSong?.uri === song?.item?.uri) {
            versionURIS.push({
              uri: `${song?.item?.uri}`,
              date: song?.added_at,
              orginalDate: lastVersionSong?.orginalDate,
              position: song?.position,
            });
            break;
          } else {
            versionURIS.push({
              uri: `${song?.item?.uri}`,
              date: song?.added_at,
              orginalDate: song?.added_at,
              position: song?.position,
            });
            break;
          }
        }
      });
      let obj = {
        version:
          refreshRecordedUris[0]?.uris[refreshRecordedUris[0]?.uris?.length - 1]?.version + 1,
        date: new Date(),
        uris: versionURIS,
      };

      updatedVersionArr.push(obj);
    } else {
      updatedVersionArr = [
        {
          version: 1,
          date: new Date(),
          uris: songUri.map((song, i) => {
            return {
              uri: `${song?.item?.uri}`,
              date: song?.added_at,
              orginalDate: song?.added_at,
              position: song?.position,
            };
          }),
        },
      ];
    }
    if (updatedVersionArr.length > 3) {
      updatedVersionArr.shift();
    }
    if (saveRestore) {
      const formData = {
        playlist_id: playlistId,
        date: moment(),
        uris: JSON.stringify(updatedVersionArr),
        addeddUri: JSON.stringify(refreshRecordedaddeddUri.concat(addeddsongUri)),
      };
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      await axios.post(`${BASE_URL}/PlaylistRefreshRecord/add`, formData, config);
    } else {
      const formData = {
        playlist_id: playlistId,
        date: moment(),
        uris: PlaylistRefreshRecord[0]?.uris,
        addeddUri: JSON.stringify(refreshRecordedaddeddUri.concat(addeddsongUri)),
      };

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      await axios.post(`${BASE_URL}/PlaylistRefreshRecord/add`, formData, config);
    }

    const songsToBeDeleted = songUris.map((song, index) => {
      return `{\"uri\":\"${song}\"}`;
    });

    await deleteAllTracks(token, playlistId, songsToBeDeleted);
    await addAllTracks(token, playlistId, songUris);

    await timeout(1000);
    window.location.reload();
  } catch (err) {
    await timeout(1000);
  }
}

export async function getSinglePageTracks(token, playlistId, pagesize, offset) {
  let resp = await axios(
    `https://api.spotify.com/v1/playlists/${playlistId}/items?fields=items(added_at%2Cadded_by.id%2Citem)&limit=50&offset=${
      offset * 50
    }`,
    {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + token,
      },
    }
  );
  return resp?.data;
}

export async function getAllTracks(token, playlistId) {
  let offset = 0;
  let pagesize = 50;
  let continueloop = true;
  let tracks = [];
  while (continueloop) {
    let next = await getSinglePageTracks(token, playlistId, pagesize, offset);
    offset++;
    if (next && next?.items.length) {
      tracks = [...tracks, ...next?.items];
    } else {
      continueloop = false;
    }
  }
  return tracks;
}

function splitArray(arr) {
  if (arr.length > 100) {
    const subArrays = [];
    for (let i = 0; i < arr.length; i += 100) {
      subArrays.push(arr.slice(i, i + 100));
    }
    return subArrays;
  } else {
    return [arr];
  }
}

export async function deleteAllTracks(token, playlistId, allSongs) {
  const songSubArrays = splitArray(allSongs);
  const promises = songSubArrays.map(async songs => {
    return await axios.delete(`https://api.spotify.com/v1/playlists/${playlistId}/items`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: `{"items":[${songs}]}`,
    });
  });

  const results = await Promise.all(promises);
  return results;
}

export async function addAllTracks(token, playlistId, allSongs) {
  const songSubArrays = splitArray(allSongs);

  const reversedArray = [...songSubArrays].reverse();
  for (let i = 0; i < reversedArray?.length; i++) {
    await axios(
      `https://api.spotify.com/v1/playlists/${playlistId}/items?position=${0}&uris=${
        reversedArray[i]
      }`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (i === reversedArray?.length - 1) {
      return [];
    }
  }
}

export function timeout(delay: number) {
  return new Promise(res => setTimeout(res, delay));
}
export const getRecents = (access_token: any, limit: any, before, after) => {
  axios(
    `https://api.spotify.com/v1/me/player/recently-played?limit=${limit}&before=${before}&after=${after}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${access_token}`,
      },
    }
  ).then(res => {});
};

export function secondsToDhms(seconds) {
  seconds = Number(seconds);
  var d = Math.floor(seconds / (3600 * 24));
  var h = Math.floor((seconds % (3600 * 24)) / 3600);
  var m = Math.floor((seconds % 3600) / 60);
  var s = Math.floor(seconds % 60);

  var dDisplay = d > 0 ? d + (d === 1 ? ' days' : '') : '';
  var hDisplay = h > 0 ? h + (h === 1 ? ' hours' : '') : '';
  var mDisplay = m > 0 ? m + (m === 1 ? ' minutes' : '') : '';
  var sDisplay = s > 0 ? s + (s === 1 ? ' seconds' : '') : '';
  return {
    dDisplay,
    hDisplay,
    mDisplay,
    sDisplay,
  };
}

export function timeDifference(startDate, nowDate) {
  // Calculate the difference in milliseconds
  let endDate =
    new Date(startDate).getMonth() === 11
      ? new Date(startDate).setMonth(0)
      : new Date(startDate).setMonth(new Date(startDate).getMonth() + 1);
  let difference = endDate - startDate - (nowDate - startDate);

  // Calculate days, hours, and minutes
  let days = Math.floor(difference / (1000 * 60 * 60 * 24));
  let hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  let minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  // Format the result
  let result = days
    ? days + ' days'
    : hours
    ? hours + ' hours'
    : minutes
    ? minutes + ' minutes'
    : '';

  // `${days}:${hours}:${minutes}`;

  return result;
}

export function clearSiteStorage() {
  // Clear local storage
  localStorage.clear();
  const alreadyVisited = localStorage.getItem('alreadyVisited');
  if (!alreadyVisited) {
    localStorage.setItem('alreadyVisited', 'true');
  }

  // Clear session storage
  sessionStorage.clear();

  // Clear cache
  if (caches) {
    caches.keys().then(function (cacheNames) {
      cacheNames.forEach(function (cacheName) {
        caches.delete(cacheName);
      });
    });
  }

  // Clear cookies
  document.cookie.split(';').forEach(function (cookie) {
    var eqPos = cookie.indexOf('=');
    var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  });
}

export const isSpotifyTrackURI = input => {
  const spotifyTrackPattern = /^spotify:track:[a-zA-Z0-9]+$/;
  return spotifyTrackPattern.test(input);
};

export const isSelected = (a, selectedArr, currentPage, i) => {
  let data = {
    uri: a?.rowNumber?.e?.item?.uri || a?.song_uri,
    positions: [
      a.lockPosition && a.lockPositionNumber
        ? Number(a.lockPositionNumber)
        : i + (currentPage - 1) * 50,
    ],
  };
  return selectedArr.filter(obj => obj?.uri === data.uri && obj?.positions[0] === data.positions[0])
    .length
    ? true
    : false;
};
export const removeDuplicates = data => {
  const seen = new Set();
  return data.filter(item => {
    const positionsKey = Array.isArray(item.positions) ? item.positions.join(',') : '';
    const key = `${item.uri}:${positionsKey}`;
    const isDuplicate = seen.has(key);
    seen.add(key);
    return !isDuplicate;
  });
};
export function deepEqual(a, b) {
  if (a === b) return true;

  if (a == null || typeof a != 'object' || b == null || typeof b != 'object') return false;

  let keysA = Object.keys(a),
    keysB = Object.keys(b);

  // eslint-disable-next-line eqeqeq
  if (keysA.length != keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }

  return true;
}

export const KeyCode = button => {
  if (navigator.platform === 'MacIntel') {
    if (button === 'delete') {
      return 8;
    } else if (button === 'ctrl') {
      return 91;
    } else if (button === 'shift') {
      return 16;
    } else if (button === 'esc') {
      return 27;
    }
  } else {
    if (button === 'delete') {
      return 46;
    } else if (button === 'ctrl') {
      return 17;
    } else if (button === 'shift') {
      return 16;
    } else if (button === 'esc') {
      return 27;
    }
  }
};

export const timeAmPM = timestamp => {
  let date = new Date(timestamp);

  // Get hours and minutes
  let hours = date.getHours();
  let minutes = date.getMinutes();

  // Determine AM/PM and convert hours to 12-hour format with zero-padding
  let period = hours >= 12 ? ' PM' : ' AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // Handle midnight (0 hours)

  // Zero-pad hours if necessary
  let formattedHours = hours < 10 ? '0' + hours : hours;

  // Zero-pad minutes if necessary
  let formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

  // Construct the formatted time string
  let formattedTime = formattedHours + ':' + formattedMinutes + period;

  return formattedTime; // Output: "05:39pm" for 17:39
};

export const truncateText = (text, maxLength) => {
  if (text?.length <= maxLength) {
    return text;
  }
  return text?.slice(0, maxLength) + '...';
};

export const getRefreshTime = (date, type, nextRefresh) => {
  if (type === 'daily') {
    return new Date(new Date(date).setDate(new Date(date).getDate() + 1)).toLocaleDateString();
  } else if (type === 'week') {
    return new Date(new Date(date).setDate(new Date(date).getDate() + 7)).toLocaleDateString();
  } else if (type === 'month') {
    return new Date(new Date(date).setMonth(new Date(date).getMonth() + 1)).toLocaleDateString();
  }
};

export const copyToClipboard = value => {
  navigator.clipboard
    .writeText(value)
    .then(() => {})
    .catch(error => {
      console.error('Failed to copy text: ', error);
    });
};

export const dateformatRough = date => {
  if (date) {
    const dateCheck = new Date(date);
    if (isNaN(dateCheck.getTime())) {
      let formatedDate = date.split('/');
      return `${formatedDate[1]}/${formatedDate[0]}/${formatedDate[2]}`;
    } else {
      return date;
    }
  }
};

export const dateformatRoughURLShare = date => {
  if (date) {
    const dateCheck = new Date(date);
    if (isNaN(dateCheck.getTime())) {
      let formatedDate = date.split('-');
      return `${formatedDate[1]}/${formatedDate[0]}/${formatedDate[2]}`;
    } else {
      return date;
    }
  }
};

// Function to get current UserProfile ObjectId
export const getCurrentUserProfileObjectId = async (): Promise<string | null> => {
  try {
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

    if (!spotifyUser?.username || !userInfo?.userId) {
      console.error('Missing spotify user or userInfo data', {
        spotifyUsername: spotifyUser?.username,
        userInfoUserId: userInfo?.userId,
      });
      return null;
    }

    // Check if currently on a shared profile
    if (spotifyUser.isShared === true && spotifyUser.profileId) {
      console.log('🔄 [getCurrentUserProfileObjectId] Using shared profile ID:', spotifyUser.profileId);
      return spotifyUser.profileId; // Return owner's profile ID directly
    }

    // Fetch all user profiles for the current Auth0 user
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.get(`${BASE_URL}/user/${userInfo.userId}/data`, config);

    const userProfiles = response?.data?.data || [];

    // Find the current profile by matching username and token
    const currentProfile = userProfiles.find((profile: any) => {
      const usernameMatch = profile.username === spotifyUser.username;
      return usernameMatch;
    });

    if (currentProfile && currentProfile._id) {
      return currentProfile._id;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching current UserProfile ObjectId:', error);
    return null;
  }
};

// Function to update user profile data
export const updateUserProfile = async (userProfileId: string, updateData: any): Promise<any> => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.put(
      `${BASE_URL}/user/${userProfileId}/update`,
      updateData,
      config
    );

    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Function to get current user's acceptCampaigns setting
export const getCurrentUserAcceptCampaigns = async (): Promise<boolean> => {
  try {
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

    if (!spotifyUser?.username || !userInfo?.userId) {
      console.error('Missing spotify user or userInfo data', {
        spotifyUsername: spotifyUser?.username,
        userInfoUserId: userInfo?.userId,
      });
      return false;
    }

    // Check if on shared profile - fetch owner's profile data
    if (spotifyUser.isShared === true && spotifyUser.ownerId && spotifyUser.profileId) {
      console.log('🔄 [getCurrentUserAcceptCampaigns] Fetching acceptCampaigns for shared profile owner');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      // Fetch owner's profiles using owner's Auth0 ID
      const response = await axios.get(`${BASE_URL}/user/${spotifyUser.ownerId}/data`, config);
      const ownerProfiles = response?.data?.data || [];
      
      // Find the specific shared profile
      const sharedProfile = ownerProfiles.find((profile: any) => 
        profile._id === spotifyUser.profileId
      );

      if (sharedProfile) {
        return sharedProfile.acceptCampaigns || false;
      }
      return false;
    }

    // Fetch all user profiles for the current Auth0 user
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await axios.get(`${BASE_URL}/user/${userInfo.userId}/data`, config);

    const userProfiles = response?.data?.data || [];

    // Find the current profile by matching username
    const currentProfile = userProfiles.find((profile: any) => {
      return profile.username === spotifyUser.username;
    });

    if (currentProfile) {
      return currentProfile.acceptCampaigns || false;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error fetching current user's acceptCampaigns setting:", error);
    return false;
  }
};
